import {
  Box,
  BoxProps,
  Button,
  CloseButton,
  Text,
  useToast,
} from "@chakra-ui/react";
import { captureException } from "@sentry/nextjs";
import { useEffect, useRef } from "react";
import { asyncVoid } from "utils/asyncVoidHandler";
import { useActiveAppAlerts } from "utils/currentUserHooks";
import { LocalStorageKeyJSON } from "utils/localStorage";
import {
  AlertStatus as AlertStatusEnum,
  AppAlertPlacement,
} from "__generated__/gql/graphql";

async function getRenderedBody(body: string | null | undefined) {
  if (!body) return;

  const renderMarkdownModule =
    await import("components/Shared/MarkdownText/render");
  return renderMarkdownModule.default(body);
}

const statusAccentColorMap: Record<AlertStatusEnum, string> = {
  [AlertStatusEnum.ERROR]: "red.400",
  [AlertStatusEnum.INFO]: "blue.400",
  [AlertStatusEnum.SUCCESS]: "green.400",
  [AlertStatusEnum.WARNING]: "orange.400",
};

const pendingAlertRenders = new Map<string, Promise<unknown>>();
const visibleAlertIds = new Set<string>();
const closedAlertIds = new Set<string>();

function DismissAlertFooter({
  onDismiss,
  ...props
}: BoxProps & { onDismiss: () => void }) {
  return (
    <Box textAlign="right" {...props}>
      <Button variant="link" size="xs" onClick={onDismiss}>
        Don’t show again
      </Button>
    </Box>
  );
}

export default function useAppAlerts() {
  const alerts = useActiveAppAlerts();

  const lsRef = useRef(new LocalStorageKeyJSON<string[]>("dismissedAppAlerts"));

  const dismissedAppAlertsRef = useRef(lsRef.current.get() || []);

  const showToast = useToast();

  useEffect(() => {
    let isCancelled = false;
    const effectAlertIds = new Set(alerts?.map((alert) => alert.id));

    if (alerts) {
      try {
        const dismissedAppAlerts = dismissedAppAlertsRef.current;
        const activeAlertIds = effectAlertIds;

        alerts.forEach((alert) => {
          if (
            dismissedAppAlerts.includes(alert.id) ||
            closedAlertIds.has(alert.id) ||
            pendingAlertRenders.has(alert.id) ||
            visibleAlertIds.has(alert.id)
          ) {
            return;
          }

          const pendingRender = getRenderedBody(alert.body);

          pendingAlertRenders.set(alert.id, pendingRender);
          asyncVoid(
            pendingRender
              .then((renderedBody) => {
                if (
                  isCancelled ||
                  pendingAlertRenders.get(alert.id) !== pendingRender
                ) {
                  return;
                }

                const closeAlert = () => showToast.close(alert.id);

                const sessionCloseAlert = () => {
                  visibleAlertIds.delete(alert.id);
                  closedAlertIds.add(alert.id);
                  closeAlert();
                };

                const dismissAlert = () => {
                  dismissedAppAlerts.push(alert.id);
                  lsRef.current.set(dismissedAppAlerts);
                  visibleAlertIds.delete(alert.id);
                  closedAlertIds.delete(alert.id);
                  closeAlert();
                };

                const showAlertToast = () => {
                  if (alert.placement === AppAlertPlacement.BOTTOM_LEFT) {
                    const accentColor =
                      alert.status === AlertStatusEnum.INFO
                        ? "gray.400"
                        : statusAccentColorMap[alert.status];

                    return showToast({
                      position: "bottom-left",
                      duration: null,
                      variant: "subtle",
                      containerStyle: {
                        width: "20rem",
                        maxWidth: "calc(100vw - 2rem)",
                      },
                      id: alert.id,
                      render: () => (
                        <Box
                          position="relative"
                          borderLeftWidth="4px"
                          borderLeftColor={accentColor}
                          borderRadius="md"
                          bg="white"
                          boxShadow="lg"
                          px={4}
                          py={3}
                          pr={10}
                        >
                          <CloseButton
                            position="absolute"
                            top={3}
                            right={3}
                            onClick={sessionCloseAlert}
                          />

                          {alert.heading ? (
                            <Text fontSize="sm" fontWeight="semibold" mb={2}>
                              {alert.heading}
                            </Text>
                          ) : null}

                          <Box fontSize="sm">{renderedBody || alert.body}</Box>

                          <DismissAlertFooter mt={3} onDismiss={dismissAlert} />
                        </Box>
                      ),
                    });
                  }

                  return showToast({
                    position: "top",
                    duration: null,
                    variant: "subtle",
                    containerStyle: { width: "50rem", maxWidth: "75vw" },
                    id: alert.id,
                    render: () => (
                      <Box
                        position="relative"
                        borderLeftWidth="4px"
                        borderLeftColor={statusAccentColorMap[alert.status]}
                        borderRadius="md"
                        bg="white"
                        boxShadow="lg"
                        px={4}
                        py={3}
                        pr={10}
                        maxWidth="50rem"
                      >
                        <CloseButton
                          position="absolute"
                          top={3}
                          right={3}
                          onClick={sessionCloseAlert}
                        />

                        {alert.heading ? (
                          <Text fontSize="sm" fontWeight="semibold" mb={2}>
                            {alert.heading}
                          </Text>
                        ) : null}

                        <Box fontSize="sm">{renderedBody || alert.body}</Box>

                        <DismissAlertFooter mt={3} onDismiss={dismissAlert} />
                      </Box>
                    ),
                  });
                };

                const toastId = showAlertToast();
                pendingAlertRenders.delete(alert.id);

                if (toastId) {
                  visibleAlertIds.add(alert.id);
                }
              })
              .catch((e) => {
                if (pendingAlertRenders.get(alert.id) === pendingRender) {
                  pendingAlertRenders.delete(alert.id);
                }

                captureException(e);
              }),
          );
        });

        Array.from(pendingAlertRenders.keys()).forEach((alertId) => {
          if (activeAlertIds.has(alertId)) return;

          pendingAlertRenders.delete(alertId);
          closedAlertIds.delete(alertId);
        });

        Array.from(visibleAlertIds).forEach((alertId) => {
          if (activeAlertIds.has(alertId)) return;

          visibleAlertIds.delete(alertId);
          closedAlertIds.delete(alertId);
          showToast.close(alertId);
        });

        Array.from(closedAlertIds).forEach((alertId) => {
          if (activeAlertIds.has(alertId)) return;

          closedAlertIds.delete(alertId);
        });
      } catch (e) {
        captureException(e);
      }
    }

    return () => {
      isCancelled = true;

      effectAlertIds.forEach((alertId) => {
        pendingAlertRenders.delete(alertId);
      });
    };
  }, [alerts, showToast]);
}
